import { useVuelidate } from '@vuelidate/core';
import { helpers, required as requiredValidator } from '@vuelidate/validators';
import { mapType } from '../../../../helpers/mappingFieldsUtils';

function isVariable(value) {
  return typeof value === 'string' && /%\{[\w.]+\}/.test(value);
}

function findNestedField(options, fieldName) {
  return options.find(opt => opt.name === fieldName || opt.value === fieldName);
}

function validateNestedField(
  parts,
  wrapper,
  getCustomObjects,
  getDynamicOptions
) {
  if (parts.length === 0) return;

  let record = null;
  if (wrapper.custom_object) {
    record = getCustomObjects(wrapper.custom_object);
  } else if (wrapper.model) {
    record = getDynamicOptions(wrapper.model);
  }

  // Record not in store yet — skip validation to avoid false errors
  if (!record) return;

  const options = record.options || [];

  const field = findNestedField(options, parts[0]);
  if (!field) {
    throw new Error(`Condition: variable '${parts[0]}' does not exist`);
  }

  // Recursively validate deeper nesting (e.g. contact.address.city)
  if (parts.length > 1 && field.wrapper) {
    validateNestedField(
      parts.slice(1),
      field,
      getCustomObjects,
      getDynamicOptions
    );
  }
}

function validateConditionVariable(
  variableName,
  variables,
  getCustomObjects,
  getDynamicOptions
) {
  if (!variables || variables.length === 0) return;

  const hasDot = variableName.includes('.');
  if (!hasDot) {
    const exists = variables.some(v => !v.wrapper && v.name === variableName);
    if (!exists) {
      throw new Error(`Condition: variable '${variableName}' does not exist`);
    }
  } else {
    const parts = variableName.split('.');
    const wrapper = variables.find(v => v.wrapper && v.name === parts[0]);
    if (!wrapper) {
      throw new Error(`Condition: variable '${variableName}' does not exist`);
    }
    validateNestedField(
      parts.slice(1),
      wrapper,
      getCustomObjects,
      getDynamicOptions
    );
  }
}

function validateCondition(
  condition,
  comparisonOperators,
  variables,
  resolvers
) {
  if (!requiredValidator.$validator(condition.variable_name)) {
    throw new Error('Condition: variable_name field is required');
  }

  validateConditionVariable(
    condition.variable_name,
    variables,
    resolvers.getCustomObjects,
    resolvers.getDynamicOptions
  );

  const comparisonOperator = condition.operator;
  if (!requiredValidator.$validator(comparisonOperator)) {
    throw new Error('Condition: operator field is required');
  }

  const comparisonOperatorRegistry = comparisonOperators.find(
    item => item.value === comparisonOperator
  );
  if (!comparisonOperatorRegistry) {
    throw new Error(
      `Condition: comparison operator '${comparisonOperator}' is invalid`
    );
  }

  if (!comparisonOperatorRegistry.no_value) {
    if (!requiredValidator.$validator(condition.value)) {
      throw new Error('Condition: value field is required');
    }
  }
}

function validateConditions(
  conditions,
  comparisonOperators,
  variables,
  resolvers
) {
  if (conditions) {
    conditions.forEach(condition => {
      validateCondition(condition, comparisonOperators, variables, resolvers);
    });
  }
}

function validateBranch(
  branch,
  logicalOperators,
  comparisonOperators,
  variables,
  resolvers
) {
  const logicalOperator = branch.logical_operator;
  if (!logicalOperator) {
    throw new Error('Condition: logical_operator field is required');
  }
  if (!logicalOperators.includes(logicalOperator)) {
    throw new Error(
      `Condition: logical operator '${logicalOperator}' is invalid. Valid operators are ${logicalOperators.join(
        ', '
      )}`
    );
  }

  validateConditions(
    branch.conditions,
    comparisonOperators,
    variables,
    resolvers
  );
}

const conditionValidation = registries =>
  helpers.withParams(
    {
      value: registries,
    },
    step => {
      try {
        const [
          logicalOperatorsRegistry,
          comparisonOperators,
          variables,
          getCustomObjects,
          getDynamicOptions,
        ] = registries;
        if (!logicalOperatorsRegistry?.length || !comparisonOperators?.length) {
          return true;
        }
        const resolvers = { getCustomObjects, getDynamicOptions };
        const logicalOperators = logicalOperatorsRegistry.map(
          item => item.value
        );
        const branches = step.branches ?? [];
        branches.forEach(branch => {
          validateBranch(
            branch,
            logicalOperators,
            comparisonOperators,
            variables,
            resolvers
          );
        });
        return true;
      } catch (error) {
        return false;
      }
    }
  );

export default {
  setup() {
    return { v$: useVuelidate() };
  },
  computed: {
    comparisonOperators() {
      return this.$store.getters[
        'wgptAutomationRegistry/getComparisonOperators'
      ];
    },
    logicalOperators() {
      return this.$store.getters['wgptAutomationRegistry/getLogicalOperators'];
    },
    customObjectsGetter() {
      return this.$store.getters['wgptAutomationRegistry/getCustomObjects'];
    },
    dynamicOptionsGetter() {
      return this.$store.getters['wgptAutomationRegistry/getDynamicOptions'];
    },
    resolvedWrapperOptions() {
      const result = {};
      const variables = this.variables || [];
      variables.forEach(v => {
        if (v.wrapper && v.custom_object) {
          const data = this.customObjectsGetter(v.custom_object);
          if (data) result[`co:${v.custom_object}`] = data;
        }
        if (v.model) {
          const data = this.dynamicOptionsGetter(v.model);
          if (data) result[`dm:${v.model}`] = data;
        }
      });
      return result;
    },
    conditionVariableTypes() {
      const types = {};
      const variables = this.variables || [];
      const branches = this.step?.branches ?? [];
      branches.forEach(branch => {
        (branch.conditions || []).forEach(condition => {
          const variableName = condition.variable_name;
          if (!variableName) return;

          const variable = variables.find(
            v => !v.wrapper && v.name === variableName
          );
          if (variable) {
            types[variableName] = mapType(variable.type);
          }
        });
      });
      return types;
    },
  },
  watch: {
    resolvedWrapperOptions: {
      handler() {
        this.$nextTick(() => {
          if (this.v$.step) {
            this.v$.step.$touch();
          }
        });
      },
      immediate: true,
    },
    conditionVariableTypes(newTypes, oldTypes) {
      if (!oldTypes) return;

      let changed = false;
      const branches = (this.step?.branches ?? []).map(branch => {
        const conditions = (branch.conditions || []).map(condition => {
          const variableName = condition.variable_name;
          if (!variableName) return condition;

          const newType = newTypes[variableName];
          const oldType = oldTypes[variableName];
          if (!oldType || !newType || oldType === newType) return condition;

          const isTextNumberSwitch =
            (oldType === 'text' && newType === 'number') ||
            (oldType === 'number' && newType === 'text');
          if (isTextNumberSwitch && isVariable(condition.value)) {
            return condition;
          }

          changed = true;
          return { ...condition, value: '' };
        });
        return { ...branch, conditions };
      });

      if (changed) {
        this.$emit('stepChange', this.step.id, {
          ...this.step,
          branches,
        });
      }
    },
  },
  validations() {
    return {
      step: {
        conditionValidation: conditionValidation([
          this.logicalOperators,
          this.comparisonOperators,
          this.variables,
          this.customObjectsGetter,
          this.dynamicOptionsGetter,
          this.resolvedWrapperOptions,
        ]),
      },
    };
  },
};
